import { useMemo, useCallback, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDrHistoryLatest, useDrHistoryRange } from './queries/drHistoryQueries';
import { useKpxSmpToday } from './queries/kpxQueries';
import { useDRResource, useRegionClusterStatPack } from './queries/drResourceQueries';
import { useOpmContractNumberInfoSummary } from './queries/resourceRelationQueries';
import { usePredictionCblPackData, usePowerMeterDemandPackData, useLivePowerMeterDemandPackData, useLiveCblDataPackData } from './queries/packStatQueries';
import { useSetUpList } from './queries/setUpQueries';
import dayjs from 'dayjs';
import { useDRSettlementForDrHistory } from './queries/drSettlementQueries';

function normalizeOpmContractSummary(raw) {
  if (raw == null || typeof raw !== 'object') return null;
  return {
    totalCount: Number(raw.TotalCount ?? raw.totalCount ?? 0),
    connectedCount: Number(raw.ConnectedCount ?? raw.connectedCount ?? 0),
    disconnectedCount: Number(raw.DisconnectedCount ?? raw.disconnectedCount ?? 0),
  };
}

/**
 * Dashboard용 실시간 데이터 훅 V2
 * DR 자원 ID 기반으로 데이터를 조회합니다.
 * @param {string} drResourceId - DR 자원 ID (필수)
 */
export const useDashboardRealtimeDataV2 = (drResourceId = null) => {
  const queryClient = useQueryClient();
  const hasErrorRef = useRef(false);

  const { data: setUpData } = useSetUpList();
  const defaultCblModuleName = setUpData?.Items?.find(item => item.Group === 'DefaultCBL' && item.Key === 'DEFAULT_MODULE_NAME')?.Value;
  const defaultCblModuleVersion = setUpData?.Items?.find(item => item.Group === 'DefaultCBL' && item.Key === 'DEFAULT_MODULE_VERSION')?.Value;

  // DR 자원 조회 (drResourceId가 있을 때만 활성화)
  const {
    data: drResourceDataById,
    isLoading: drResourceLoadingById,
    error: drResourceErrorById,
    refetch: refetchDrResourceById
  } = useDRResource(drResourceId, { enabled: !!drResourceId });

  const {
    data: opmContractSummaryRaw,
    isLoading: opmContractSummaryLoading,
    error: opmContractSummaryError,
    refetch: refetchOpmContractSummary,
  } = useOpmContractNumberInfoSummary(
    { DrResourceSysId: drResourceId },
    { enabled: !!drResourceId }
  );

  const opmContractSummary = useMemo(
    () => normalizeOpmContractSummary(opmContractSummaryRaw),
    [opmContractSummaryRaw]
  );

  // DR 자원 데이터를 Items 배열 형태로 변환
  const drResourceData = useMemo(() => {
    if (!drResourceDataById) return { Items: [] };
    return { Items: [drResourceDataById] };
  }, [drResourceDataById]);

  // DR 자원에서 충전소 목록 추출
  const stationSysIdList = useMemo(() => {
    if (!drResourceData?.Items || !Array.isArray(drResourceData.Items)) {
      return [];
    }
    
    const stationIds = new Set();
    drResourceData.Items.forEach(resource => {
      if (resource.Stations && Array.isArray(resource.Stations)) {
        resource.Stations.forEach(station => {
          if (station.StationSysId) {
            stationIds.add(station.StationSysId);
          }
        });
      }
    });
    
    return Array.from(stationIds);
  }, [drResourceData]);

  // 데이터 조회 기준일
  const targetDate = dayjs().format('YYYY-MM-DD');
  
  // 00시 예측 CBL 조회 (drResourceId가 있고 충전소가 있을 때만)
  const {
    data: predictionCblData,
    isLoading: predictionCblLoading,
    error: predictionCblError,
    refetch: refetchPredictionCbl
  } = usePredictionCblPackData(
    stationSysIdList, 
    targetDate, 
    defaultCblModuleName, 
    defaultCblModuleVersion,
    { enabled: !!drResourceId && stationSysIdList.length > 0 }
  );
  
  // 실시간 예측 CBL (drResourceId가 있고 충전소가 있을 때만)
  const {
    data: liveCblData,
    isLoading: liveCblLoading,
    error: liveCblError,
    refetch: refetchLiveCbl
  } = useLiveCblDataPackData(
    stationSysIdList, 
    targetDate, 
    defaultCblModuleName, 
    defaultCblModuleVersion,
    { enabled: !!drResourceId && stationSysIdList.length > 0 }
  );

  // 어제 전력 수요 조회 (drResourceId가 있고 충전소가 있을 때만)
  const {
    data: powerMeterDemandData,
    isLoading: powerMeterDemandLoading,
    error: powerMeterDemandError,
    refetch: refetchPowerMeterDemand
  } = usePowerMeterDemandPackData(
    stationSysIdList, 
    dayjs(targetDate).subtract(1, 'day').format('YYYY-MM-DD'),
    { enabled: !!drResourceId && stationSysIdList.length > 0 }
  );
  
  // 실시간 전력 수요 조회 (drResourceId가 있고 충전소가 있을 때만)
  const {
    data: livePowerMeterDemandData,
    isLoading: livePowerMeterDemandLoading,
    error: livePowerMeterDemandError,
    refetch: refetchLivePowerMeterDemand
  } = useLivePowerMeterDemandPackData(
    stationSysIdList, 
    targetDate,
    { enabled: !!drResourceId && stationSysIdList.length > 0 }
  );

  // DR 발령 이력 데이터 (항상 로드)
  const {
    data: drHistoryLatestData,
    isLoading: drHistoryLatestLoading,
    error: drHistoryLatestError,
    refetch: refetchDrHistoryLatest
  } = useDrHistoryLatest();

  // 최근 DR 발령일 정산 데이터 조회
  const {
    data: drHistoryLatestSettlementData,
    isLoading: drHistoryLatestSettlementLoading,
    error: drHistoryLatestSettlementError,
    refetch: refetchDrHistoryLatestSettlement
  } = useDRSettlementForDrHistory({
    TargetDate: dayjs(drHistoryLatestData?.date).format('YYYY-MM-DD'),
    StartEndTime: `${drHistoryLatestData?.startTime.replace(':', '')}-${drHistoryLatestData?.endTime.replace(':', '')}`,
  });

  // DR 발령 이력 데이터 (범위) (항상 로드)
  const {
    data: drHistoryRangeData,
    isLoading: drHistoryRangeLoading,
    error: drHistoryRangeError,
    refetch: refetchDrHistoryRange
  } = useDrHistoryRange({
    startDate: dayjs().subtract(3, 'month').format('YYYYMMDD'),
    endDate: dayjs().format('YYYYMMDD')
  });

  // SMP 데이터 (항상 로드)
  const {
    data: kpxSmpRangeData,
    isLoading: kpxSmpRangeLoading,
    error: kpxSmpRangeError,
    refetch: refetchKpxSmpRange
  } = useKpxSmpToday(
    {
      startDate: dayjs().format('YYYY-MM-DD'),
      areaCode: 9
    }
  );

  // 지역별 지역 클러스터 정보 (drResourceId가 있고 충전소가 있을 때만)
  const {
    data: regionClusterStatData,
    isLoading: regionClusterStatLoading,
    error: regionClusterStatError,
    refetch: refetchRegionClusterStat
  } = useRegionClusterStatPack({
    targetDate,
    StationSysIdList: stationSysIdList
  }, { enabled: !!drResourceId && stationSysIdList.length > 0 });

  // 최근 DR 발령일 전력 수요 조회 (drResourceId가 있고 충전소가 있을 때만)
  const {
    data: drEventPowerMeterDemandData,
    isLoading: drEventPowerMeterDemandLoading,
    error: drEventPowerMeterDemandError,
    refetch: refetchDrEventPowerMeterDemand
  } = usePowerMeterDemandPackData(
    stationSysIdList, 
    dayjs(drHistoryLatestData?.date).format('YYYY-MM-DD'),
    { enabled: !!drResourceId && stationSysIdList.length > 0 }
  );

  // 통합 로딩 상태
  const isLoading = drHistoryLatestLoading || drHistoryRangeLoading || kpxSmpRangeLoading || 
    drResourceLoadingById ||
    (!!drResourceId && opmContractSummaryLoading) ||
    (stationSysIdList.length > 0 && (predictionCblLoading || powerMeterDemandLoading || livePowerMeterDemandLoading || drEventPowerMeterDemandLoading || regionClusterStatLoading));

  // 통합 에러 상태
  const hasError = !!(
    drResourceErrorById ||
    drHistoryLatestError || 
    drHistoryRangeError || 
    kpxSmpRangeError || 
    regionClusterStatError ||
    (stationSysIdList.length > 0 && (predictionCblError || powerMeterDemandError || livePowerMeterDemandError || drEventPowerMeterDemandError))
  );

  // 개별 에러 상태 추적
  const errorStates = useMemo(() => ({
    drResourceError: !!drResourceErrorById,
    drHistoryLatestError: !!drHistoryLatestError,
    drHistoryRangeError: !!drHistoryRangeError,
    kpxSmpRangeError: !!kpxSmpRangeError,
    regionClusterStatError: !!regionClusterStatError,
    predictionCblError: stationSysIdList.length > 0 && !!predictionCblError,
    powerMeterDemandError: stationSysIdList.length > 0 && !!powerMeterDemandError,
    livePowerMeterDemandError: stationSysIdList.length > 0 && !!livePowerMeterDemandError,
    drEventPowerMeterDemandError: stationSysIdList.length > 0 && !!drEventPowerMeterDemandError,
  }), [
    drResourceErrorById,
    drHistoryLatestError,
    drHistoryRangeError,
    kpxSmpRangeError,
    regionClusterStatError,
    predictionCblError,
    powerMeterDemandError,
    livePowerMeterDemandError,
    drEventPowerMeterDemandError,
    stationSysIdList.length,
  ]);

  // 에러 발생 시 선택적 처리
  useEffect(() => {
    if (errorStates.drResourceError) {
      queryClient.setQueryDefaults(['drResource'], { 
        retry: 1,
        refetchOnWindowFocus: false 
      });
    }
    if (errorStates.drHistoryLatestError || errorStates.drHistoryRangeError) {
      queryClient.setQueryDefaults(['drHistory'], { 
        retry: 1,
        refetchOnWindowFocus: false 
      });
    }
    if (errorStates.kpxSmpRangeError) {
      queryClient.setQueryDefaults(['kpx'], { 
        retry: 1,
        refetchOnWindowFocus: false 
      });
    }
    if (errorStates.predictionCblError || errorStates.powerMeterDemandError || 
        errorStates.livePowerMeterDemandError || errorStates.drEventPowerMeterDemandError) {
      queryClient.setQueryDefaults(['packStat'], { 
        retry: 1,
        refetchOnWindowFocus: false 
      });
    }
    if (errorStates.regionClusterStatError) {
      queryClient.setQueryDefaults(['regionCluster'], { 
        retry: 1,
        refetchOnWindowFocus: false 
      });
    }

    // 에러가 해결된 쿼리는 기본 설정으로 복구
    if (!errorStates.drResourceError) {
      queryClient.setQueryDefaults(['drResource'], {});
    }
    if (!errorStates.drHistoryLatestError && !errorStates.drHistoryRangeError) {
      queryClient.setQueryDefaults(['drHistory'], {});
    }
    if (!errorStates.kpxSmpRangeError) {
      queryClient.setQueryDefaults(['kpx'], {});
    }
    if (!errorStates.predictionCblError && !errorStates.powerMeterDemandError && 
        !errorStates.livePowerMeterDemandError && !errorStates.drEventPowerMeterDemandError) {
      queryClient.setQueryDefaults(['packStat'], {});
    }
    if (!errorStates.regionClusterStatError) {
      queryClient.setQueryDefaults(['regionCluster'], {});
    }
  }, [errorStates, queryClient]);

  // refetch 함수들을 ref로 저장하여 안정화
  const refetchFunctionsRef = useRef({
    refetchDrHistoryLatest,
    refetchDrHistoryRange,
    refetchKpxSmpRange,
    refetchDrResourceById,
    refetchOpmContractSummary,
    refetchPredictionCbl,
    refetchPowerMeterDemand,
    refetchLivePowerMeterDemand,
    refetchDrEventPowerMeterDemand,
    refetchRegionClusterStat,
    refetchDrHistoryLatestSettlement
  });

  // refetch 함수들 업데이트
  useEffect(() => {
    refetchFunctionsRef.current = {
      refetchDrHistoryLatest,
      refetchDrHistoryRange,
      refetchKpxSmpRange,
      refetchDrResourceById,
      refetchOpmContractSummary,
      refetchPredictionCbl,
      refetchPowerMeterDemand,
      refetchLivePowerMeterDemand,
      refetchDrEventPowerMeterDemand,
      refetchRegionClusterStat,
      refetchDrHistoryLatestSettlement
    };
  }, [
    refetchDrHistoryLatest,
    refetchDrHistoryRange,
    refetchKpxSmpRange,
    refetchDrResourceById,
    refetchOpmContractSummary,
    refetchPredictionCbl,
    refetchPowerMeterDemand,
    refetchLivePowerMeterDemand,
    refetchDrEventPowerMeterDemand,
    refetchRegionClusterStat,
    refetchDrHistoryLatestSettlement
  ]);

  // 실시간 데이터 새로고침 함수
  const refetchAll = useCallback(async () => {
    const refs = refetchFunctionsRef.current;
    const promises = [];
    
    if (refs.refetchDrHistoryLatest) {
      promises.push(refetchDrHistoryLatest());
    }
    
    if (refs.refetchDrHistoryRange) {
      promises.push(refetchDrHistoryRange());
    }

    if (refs.refetchKpxSmpRange) {
      promises.push(refetchKpxSmpRange());
    }

    if (refs.refetchDrResourceById) {
      promises.push(refetchDrResourceById());
    }

    if (drResourceId && refs.refetchOpmContractSummary) {
      promises.push(refs.refetchOpmContractSummary());
    }

    // stationSysIdList가 비어있을 때는 pack 관련 refetch 하지 않음
    if (stationSysIdList.length > 0) {
      if (refs.refetchPredictionCbl) {
        promises.push(refetchPredictionCbl());
      }

      if (refs.refetchPowerMeterDemand) {
        promises.push(refetchPowerMeterDemand());
      }

      if (refs.refetchLivePowerMeterDemand) {
        promises.push(refetchLivePowerMeterDemand());
      }

      if (refs.refetchDrEventPowerMeterDemand) {
        promises.push(refetchDrEventPowerMeterDemand());
      }

      if (refs.refetchRegionClusterStat) {
        promises.push(refetchRegionClusterStat());
      }

      if (refs.refetchDrHistoryLatestSettlement) {
        promises.push(refetchDrHistoryLatestSettlement());
      }
    }

    // 모든 refetch 완료 대기
    const results = await Promise.allSettled(promises);
    
    // 실패한 refetch 확인 (로깅용)
    const failedRefetches = results
      .map((result, index) => ({ result, index }))
      .filter(({ result }) => result.status === 'rejected');
    
    if (failedRefetches.length > 0) {
      console.warn(`[useDashboardRealtimeDataV2] ${failedRefetches.length}개의 API refetch 실패 (일부 성공 가능)`);
      failedRefetches.forEach(({ result, index }) => {
        console.warn(`[useDashboardRealtimeDataV2] Refetch ${index} 실패:`, result.reason);
      });
    }
    
  }, [
    stationSysIdList.length,
    drResourceId,
    refetchDrHistoryLatest,
    refetchDrHistoryRange,
    refetchKpxSmpRange,
    refetchDrResourceById,
    refetchOpmContractSummary,
    refetchPredictionCbl,
    refetchPowerMeterDemand,
    refetchLivePowerMeterDemand,
    refetchDrEventPowerMeterDemand,
    refetchRegionClusterStat,
  ]);

  // 반환 객체를 useMemo로 안정화
  return useMemo(() => ({
    defaultCblModuleName,
    defaultCblModuleVersion,
    // 실시간 데이터
    drResourceData,
    stationSysIdList,
    predictionCblData: drResourceId ? predictionCblData : undefined,
    liveCblData: drResourceId ? liveCblData : undefined,
    powerMeterDemandData: drResourceId ? powerMeterDemandData : undefined,
    livePowerMeterDemandData: drResourceId ? livePowerMeterDemandData : undefined,
    drHistoryLatestData,
    drHistoryRangeData,
    kpxSmpRangeData,
    regionClusterStatData: drResourceId ? regionClusterStatData : undefined,
    drEventPowerMeterDemandData: drResourceId ? drEventPowerMeterDemandData : undefined,
    drHistoryLatestSettlementData: drResourceId ? drHistoryLatestSettlementData : undefined,
    opmContractSummary: drResourceId ? opmContractSummary : null,
    // 로딩 상태
    isLoading,
    drResourceLoading: drResourceLoadingById,
    predictionCblLoading,
    liveCblLoading,
    powerMeterDemandLoading,
    livePowerMeterDemandLoading,
    drHistoryLatestLoading,
    drHistoryRangeLoading,
    kpxSmpRangeLoading,
    regionClusterStatLoading,
    drEventPowerMeterDemandLoading,
    drHistoryLatestSettlementLoading,
    opmContractSummaryLoading: !!drResourceId && opmContractSummaryLoading,
    // 에러 상태
    hasError,
    drResourceError: drResourceErrorById,
    predictionCblError,
    liveCblError,
    powerMeterDemandError,
    livePowerMeterDemandError,
    drHistoryLatestError,
    drHistoryRangeError,
    kpxSmpRangeError,
    regionClusterStatError,
    drEventPowerMeterDemandError,
    drHistoryLatestSettlementError,
    opmContractSummaryError: !!drResourceId ? opmContractSummaryError : null,
    // 새로고침 함수
    refetchAll,
    refetchDrResource: refetchDrResourceById,
    refetchPredictionCbl,
    refetchLiveCbl,
    refetchPowerMeterDemand,
    refetchLivePowerMeterDemand,
    refetchDrHistoryLatest,
    refetchDrHistoryRange,
    refetchKpxSmpRange,
    refetchRegionClusterStat,
    refetchDrEventPowerMeterDemand,
    refetchDrHistoryLatestSettlement,
    refetchOpmContractSummary,
  }), [
    // 데이터
    defaultCblModuleName,
    defaultCblModuleVersion,
    drResourceData,
    stationSysIdList,
    predictionCblData,
    liveCblData,
    powerMeterDemandData,
    livePowerMeterDemandData,
    drHistoryLatestData,
    drHistoryRangeData,
    kpxSmpRangeData,
    regionClusterStatData,
    drEventPowerMeterDemandData,
    drHistoryLatestSettlementData,
    opmContractSummary,
    // 로딩 상태
    isLoading,
    drResourceLoadingById,
    predictionCblLoading,
    liveCblLoading,
    powerMeterDemandLoading,
    livePowerMeterDemandLoading,
    drHistoryLatestLoading,
    drHistoryRangeLoading,
    kpxSmpRangeLoading,
    regionClusterStatLoading,
    drEventPowerMeterDemandLoading,
    drHistoryLatestSettlementLoading,
    opmContractSummaryLoading,
    // 에러 상태
    hasError,
    drResourceErrorById,
    predictionCblError,
    liveCblError,
    powerMeterDemandError,
    livePowerMeterDemandError,
    drHistoryLatestError,
    drHistoryRangeError,
    kpxSmpRangeError,
    regionClusterStatError,
    drEventPowerMeterDemandError,
    drHistoryLatestSettlementError,
    opmContractSummaryError,
    // 함수
    refetchAll,
    refetchOpmContractSummary,
    drResourceId,
  ]);
};

export default useDashboardRealtimeDataV2;
